import { createApiClient } from '../api/client';
import type { MeResponse } from '../api/types';

/** The React Query key for `GET /me`. Identity-scoped, so it is deliberately NOT a
 *  `workspaceKey`: one user, one answer, whichever workspace is active. */
export const ME_QUERY_KEY = ['me'] as const;

/**
 * The ONE definition of the {@link ME_QUERY_KEY} query (steward-app#673).
 *
 * Five observers used to declare this key inline (`WorkspaceProvider`, `FirstRunGate`,
 * `OnboardingClientGate`, `useOnboardingSelfLink`, `useOnboardingTaxIdCopy`), each with its
 * own copy of the `queryFn`. React Query keeps one `data` value and one set of options per
 * key, so the last observer to register wins for the next fetch, and two `queryFn`s that
 * disagree about what they return each read what the other last wrote (steward-app#671: an
 * array in one, an object in the other, and a `TypeError` on the page that guessed wrong).
 * The five copies were identical when they were collapsed, so nothing was broken; the point
 * is that one more edit to one of them would have broken the rest without any test saying so.
 *
 * `src/lib/queryKeyDeclarations.test.ts` is what keeps this one. A projection a caller wants
 * belongs in React Query's per-observer `select`, which never changes what is cached.
 *
 * `enabled` is the only thing that differed between the five (`FirstRunGate` passes its own
 * flag, the rest were `!!token`), and the default keeps every other caller byte-for-byte
 * what it was.
 */
export function meQueryOptions(token: string | null, enabled = true) {
  return {
    queryKey: ME_QUERY_KEY,
    queryFn: () => createApiClient(token!).get<MeResponse>('/me'),
    enabled: enabled && !!token,
    staleTime: 5 * 60_000,
    retry: false as const,
  };
}
